import { Capacitor } from "@capacitor/core";

import config from "../settings/config";
import { getAuthToken } from "./authStorage";

/**
 * Hosts that belong to us: the SPA builds, and the backends an action may
 * legitimately be called against.
 *
 * Matched by HOST, not origin, and hard-coded across environments rather than
 * read only from the current one. Both details matter: production sends
 * `link_url` on `app.dietasocial.it` (40k+ notifications and counting), but the
 * native build's configured app origin is `https://localhost` — so an
 * env-derived origin list would classify every one of those as external and
 * bounce users out to a browser instead of navigating in-app.
 *
 * The apex `dietasocial.it` is deliberately NOT here: it serves the marketing
 * site, not the SPA, so a link to it genuinely does belong in a browser.
 */
const INTERNAL_HOSTS = new Set([
  // SPA builds
  "app.dietasocial.it",
  "webapp.dietasocial.it",
  "native.dietasocial.it",
  "sandbox.dietasocial.it",
  "old-app.dietasocial.it",
  // backends / APIs an absolute action URL may target
  "simon.dietasocial.it",
  "stage.dietasocial.it",
  "stage-api.dietasocial.it",
  "api.dietasocial.it",
  // local development and the Capacitor WebView
  "localhost",
  "127.0.0.1",
]);

/** Our hosts, plus whatever this particular build is configured for. */
const ownHosts = () => {
  const env = config?.env?.[config?.env?.mode] || {};
  const hosts = new Set(INTERNAL_HOSTS);

  [env.url, env.node, env.app].filter(Boolean).forEach((u) => {
    try {
      hosts.add(new URL(u).hostname);
    } catch {
      // malformed config entry — the hard-coded list still applies
    }
  });

  try {
    hosts.add(window.location.hostname);
  } catch {
    // no window (tests) — the list above is enough
  }

  return hosts;
};

/**
 * True when the URL points somewhere that is not ours.
 *
 * The CRM requires a `verb` whenever an action has a URL
 * (Actions/Editor: `verb => required_with:url`), so the "absolute URL without
 * verb" branch below was unreachable for anything an operator could actually
 * create. An external campaign link therefore got fetched as if it were our own
 * API: the request succeeded, the action reported success, and nothing opened.
 * Deciding by origin rather than by the presence of a verb fixes that without
 * needing every existing action to be re-saved.
 */
export const isExternalActionUrl = (url) => {
  if (!/^https?:\/\//i.test(String(url || ""))) return false;

  try {
    return !ownHosts().has(new URL(url).hostname);
  } catch {
    return false;
  }
};

/** Open a link outside the SPA: in-app browser on native, new tab on web. */
export const openExternalUrl = async (url) => {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url });
      return;
    } catch {
      // fall through to window.open
    }
  }
  window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Handle a notification CTA action.
 *
 * Rules (confirmed by backend, refined to match actual payloads):
 * - **Relative URL** (starts with `/`) → in-app navigation via `history.push`,
 *   regardless of `verb`. The backend has been observed sending `verb: "GET"`
 *   on deep-link actions like `{ url: "/dirette/4", verb: "GET" }` for live
 *   streaming events; treating that as an API call (the previous behaviour)
 *   would `fetch("/dirette/4")`, hit the WebView's own static HTML, return
 *   200, and report "success" without navigating anywhere.
 * - **Absolute URL with `verb`** → call as a backend API endpoint.
 * - **Absolute URL without `verb`** → open as an external link.
 *
 * @param {Object} action - The action object (flat or with `.attributes`)
 * @param {Object} history - React Router history for internal navigation
 * @returns {Promise<{ success?: boolean, navigated?: boolean, error?: string }>}
 */
/**
 * A challenge reminder's "Conferma oggi" action. These are confirmed with the
 * in-app ActivityConfirmModal (no deep-link / redirect) — the app posts directly
 * to the challenge (no push_notification_action), so only `challenge_id` is needed.
 */
export const isChallengeConfirmAction = (action) => {
  const a = action?.attributes || action || {};
  return a.style === "confirm" && !!a.challenge_id;
};


/**
 * Record that the member acted on a notification.
 *
 * Only for actions that open something — a page in the app, or a link. An
 * "answer" action (Fatto / Ho dimenticato) already IS a call to this endpoint,
 * because the CRM stores the reply URL as the action's own url, so recording it
 * again here would double-count.
 *
 * That asymmetry is the bug this closes: Risposte counted answers and nothing
 * else, so a notification whose CTA opened the percorso gratuito reported zero
 * interactions no matter how many people tapped it.
 *
 * Fire-and-forget on purpose. The member asked to go somewhere; a failed count
 * must never delay or block the navigation, and `deliverId` makes the call
 * idempotent server-side, so a retry is unnecessary.
 */
const recordActionTap = (action, deliverId) => {
  const attrs = action?.attributes || action || {};
  const actionId = attrs.id ?? action?.id;

  if (!actionId) return;

  const baseUrl = config?.env?.[config?.env?.mode]?.url;
  if (!baseUrl) return;

  try {
    fetch(`${baseUrl}/api/v1/push-notifications/actions/${actionId}/reply`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify(deliverId ? { deliver_id: deliverId } : {}),
    }).catch(() => {});
  } catch {
    /* counting is best-effort — never let it affect the navigation */
  }
};

export const handleNotificationAction = async (action, history, options = {}) => {
  const attrs = action?.attributes || action;
  const { url, verb, data, payload } = attrs;
  const deliverId = options?.deliverId ?? attrs?.deliver_id ?? null;

  if (!url) return { error: "Nessun URL specificato" };

  const isAbsoluteUrl = /^https?:\/\//i.test(url);

  try {
    // The operator ticked "Link esterno" in the CRM. Explicit intent wins over
    // everything below — including the host check, which cannot know that
    // e.g. simon.dietasocial.it/new/streaming/live/11/viewer is a web page
    // rather than an API endpoint.
    if (attrs?.is_external && isAbsoluteUrl) {
      recordActionTap(action, deliverId);
      await openExternalUrl(url);
      return { navigated: true };
    }

    // Relative URL → always treat as in-app navigation. `verb` is irrelevant
    // here because the path is owned by the SPA router, not the backend API.
    if (!isAbsoluteUrl) {
      recordActionTap(action, deliverId);
      history.push(url);
      return { navigated: true };
    }

    // No explicit flag (actions created before the field existed): fall back to
    // the host. Checked before the verb branch because the CRM always sets a
    // verb, which is what made external campaign links silently do nothing.
    if (isExternalActionUrl(url)) {
      recordActionTap(action, deliverId);
      await openExternalUrl(url);
      return { navigated: true };
    }

    // Absolute URL on one of our own hosts, with `verb` → backend API call.
    if (verb) {
      const body = payload || data;
      const res = await fetch(url, {
        method: verb,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: verb !== "GET" ? JSON.stringify(body) : undefined,
      });

      if (!res.ok) {
        const json = await res.json().catch(() => null);
        // Backend Domain/Validation exceptions are returned as
        // `{ "error": { "message": "..." } }` (e.g. challenge confirmation 422s
        // like "La sfida è appena iniziata. Riprova domani!"). Some endpoints
        // also return a flat `{ "message": "..." }`. Check both, then fall back
        // to a generic Italian message — never expose a bare HTTP status code
        // like "Errore 422" to the user, since it leaks no actionable info.
        const backendMessage =
          json?.error?.message ||
          json?.message ||
          "Si è verificato un errore. Riprova più tardi.";
        return { error: backendMessage };
      }
      return { success: true };
    }

    // Absolute URL on one of our own hosts with no verb: not an API call, so
    // treat it as a deep link and route on its path — opening our own site in a
    // browser tab would drop the user out of the app for no reason.
    try {
      recordActionTap(action, deliverId);
      const { pathname, search, hash } = new URL(url);
      const path = `${pathname}${search}${hash}`;
      history.push(path && path !== "/" ? path : "/home");
      return { navigated: true };
    } catch {
      await openExternalUrl(url);
      return { navigated: true };
    }
  } catch (e) {
    return { error: e?.message || "Errore sconosciuto" };
  }
};
